import Swal from "sweetalert2";
import {isPlainObject} from "lodash";
import {DENMARK, COUNTRY_CODE_DENMARK} from "./constants";

export const validateEmail = (email) => {
    if (!email) return false;

    return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/i.test(String(email).trim());
};

export const validatePhone = (phone, countryCode = 'DK') => {
    const cleaned = phone.replace(/[\s-]/g, '');
    let patterns = [];

    if (countryCode === COUNTRY_CODE_DENMARK) {
        patterns = [
            /^\+45\d{8}$/,   // +45 followed by 8 digits
            /^45\d{8}$/,     // 45 followed by 8 digits
            /^\d{8}$/        // just 8 digits
        ];
    } else {
        patterns = [
            /^\+46\d{7,13}$/,   // e.g. +46701234567 (mobile)
            /^46\d{7,13}$/,     // e.g. 46701234567 (mobile)
            /^\d{7,13}$/        // e.g. 01234567 (domestic, with leading zero)
        ];
    }

    return patterns.some(pattern => pattern.test(cleaned));
}

export const validateEan = (ean, checkOnlySpecialCharacter = true, productCountry = DENMARK) => {
    let invalidPatterns = [
        {pattern: /[!@$^%+=#&*():;.<>?,"']/i, message: 'Special character is not allowed'}
    ];

    // Check for other simple patterns
    if (!checkOnlySpecialCharacter) {
        invalidPatterns = [ ...invalidPatterns,
            {pattern: /^(\d)\1+$/, message: 'All digits are the same'},
            {pattern: /^\d{13}$/, message: 'Alternating pattern detected'}
        ];
    }

    for (const { pattern, message } of invalidPatterns) {
        if (pattern.test(ean)) {
            if (message === 'Alternating pattern detected') {
                if (new Set(ean).size === 2) {
                    return { valid: false, error: `Invalid EAN: ${message}` };
                }
            } else {
                return { valid: false, error: `Invalid EAN: ${message}` };
            }
        }
    }

    if (productCountry === DENMARK && !validateEANCheckDigit(ean)) {
        return {valid: false, error: 'Invalid EAN: Check digit is invalid'};
    }

    return {valid: true};
}
export const validateEANCheckDigit = (ean)=>  {
    if (!/^\d+$/.test(ean)) return false;

    const digits = ean.split('').map(Number);
    const checkDigit = digits.pop();

    let total = 0;
    for (let i = digits.length - 1; i >= 0; i--) {
        const weight = (digits.length - 1 - i) % 2 === 0 ? 3 : 1;
        total += digits[i] * weight;
    }

    const calculatedCheck = (10 - (total % 10)) % 10;
    return calculatedCheck === checkDigit;
}

export const parseCVR = (cvr) => {
    if (isPlainObject(cvr)) {
        let cvr_data = [];

        if (cvr['hits'] && cvr['hits']['hits'] && cvr['hits']['hits'].length > 0 && cvr['hits']['hits'][0]['_source'] && cvr['hits']['hits'][0]['_source']['Vrvirksomhed']) {
            let data_vrvirksomhed = cvr['hits']['hits'][0]['_source']['Vrvirksomhed'];

            if (data_vrvirksomhed['navne'] && data_vrvirksomhed['beliggenhedsadresse']) {
                if (data_vrvirksomhed['navne'].length && data_vrvirksomhed['beliggenhedsadresse'].length) {
                    let company_name_data = data_vrvirksomhed['navne'][data_vrvirksomhed['navne'].length - 1];
                    let location_address_data = data_vrvirksomhed['beliggenhedsadresse'][data_vrvirksomhed['beliggenhedsadresse'].length - 1];
                    const floor = location_address_data['etage'] === null || location_address_data['etage'] === "" ? "" : ", " + location_address_data['etage'];
                    const vejnavn = location_address_data['vejnavn'] === null || location_address_data['vejnavn'] === "" ? "" : location_address_data['vejnavn'];
                    const husnummerFra = location_address_data['husnummerFra'] === null || location_address_data['husnummerFra'] === "" ? "" : " " + location_address_data['husnummerFra'];
                    const bynavn = location_address_data['bynavn'] === null || location_address_data['bynavn'] === "" ? "" : " " + location_address_data['bynavn'];
                    let cityName = "";

                    if (location_address_data['postdistrikt'] !== null) {
                        cityName = location_address_data['postdistrikt'];
                    } else if (location_address_data['kommune']?.kommuneNavn !== null) {
                        cityName = location_address_data['kommune']?.kommuneNavn;
                    }

                    cvr_data['company_name'] = company_name_data['navn'];
                    cvr_data['opportunity_address_country'] = (location_address_data['landekode'] === 'DK') ? "Denmark" : location_address_data['landekode'];
                    cvr_data['opportunity_address_city'] = cityName; // => City Name
                    cvr_data['opportunity_address_postal_code'] = location_address_data['postnummer']; // => ZipCode
                    cvr_data['opportunity_address_1'] = vejnavn + husnummerFra + bynavn + floor;

                    return cvr_data;
                }
            } else {
                return false;
            }
        } else {
            return false;
        }
    } else {
        return false;
    }
}

export const checkIfPersonFoodConsiderationHasChanged = (initialData, formRef, foodConsiderations, component, props) => {
    const formValues = component === 'event_overview' ? formRef.obj : formRef.current.values
    let currentTypeValue = component === 'opportunity' ?
        initialData.opportunity_food_considerations_1 : initialData.person_food_considerations_1;
    let currentAllergiesValue = component === 'opportunity' ? initialData.opportunity_food_considerations_2 : initialData.person_food_considerations_2;
    let updatedTypeValue = formValues.person_food_considerations_1;
    let updatedAllergiesValue = formValues.person_food_considerations_2;
    let text = "Are you sure you want to change the food considerations?<br><br>";
    let currentFoodConsiderationsOptionValue = '';
    let updatedFoodConsiderationsOptionValue = '';
    let formValuesChanged = false;

    if (currentTypeValue !== "" && currentTypeValue !== formValues.person_food_considerations_1) {
        formValuesChanged = true;
    }

    if (currentAllergiesValue.length > 0 && JSON.stringify(currentAllergiesValue) !== JSON.stringify(formValues.person_food_considerations_2)) {
        formValuesChanged = true;
    }

    if (formValuesChanged) {
        if (currentTypeValue !== "") {
            currentFoodConsiderationsOptionValue += foodConsiderations?.type1_food?.find(item => item.id == currentTypeValue)?.title;
        }

        if (currentAllergiesValue?.length > 0) {
            for (let i = 0; i < currentAllergiesValue.length; i++) {
                currentFoodConsiderationsOptionValue += ", " + foodConsiderations?.type2_food?.find(item => item.id == currentAllergiesValue[i])?.title;
            }
        }

        updatedFoodConsiderationsOptionValue = foodConsiderations?.type1_food?.find(item => item.id == updatedTypeValue)?.title;
        updatedFoodConsiderationsOptionValue = (updatedFoodConsiderationsOptionValue === '' || updatedFoodConsiderationsOptionValue === undefined)
            ? 'None' : updatedFoodConsiderationsOptionValue;

        if (updatedAllergiesValue?.length > 0) {
            for (let i = 0; i < updatedAllergiesValue.length; i++) {
                updatedFoodConsiderationsOptionValue += ", " + foodConsiderations?.type2_food?.find(item => item.id == updatedAllergiesValue[i])?.title;
            }
        }

        text += "<b>Current:</b> " + (currentFoodConsiderationsOptionValue === '' ? 'None, None' : currentFoodConsiderationsOptionValue) + "<br><hr>";

        if (updatedFoodConsiderationsOptionValue !== '') {
            text += "<b>Updated:</b> " + updatedFoodConsiderationsOptionValue + "<br>";
        }

        Swal.fire({
            title: 'Food Considerations',
            icon: 'warning',
            html: text,
            showCancelButton: true,
            focusConfirm: false,
            confirmButtonText: 'Yes, Save Changes!',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#2e7d32',
            allowOutsideClick: false,
            allowEscapeKey: false,
            allowEnterKey: false,
        }).then((result) => {
            if (result.value) {
                if (component === 'contact') {
                    formRef.current.handleSubmit();
                } else if (component === 'opportunity' && props !== undefined) {
                    props.postOpportunityData({formData: formValues, id: initialData.opportunity_id});
                } else if (component === 'event_overview' && props !== undefined) {
                    props.updateContact(formRef);
                }
            }
        });
    }

    return formValuesChanged;
}